import { Link } from "react-router-dom";
import { GateChip, moduleGates } from "../components/Gates";
import { Icon } from "../components/Icon";
import { formatMinutes, formatPercent, pluralise } from "../lib/format";
import { useModuleCtx } from "./ModuleShell";

export default function ModuleOverview() {
  const { content, progress } = useModuleCtx();
  const { module, lessons, quiz, lab } = content;
  const base = `/modules/${module.id}`;

  const lessonsDone = Object.keys(progress.lessons).length;
  const gates = moduleGates(module, {
    lessonsDone,
    lessonsTotal: lessons.length,
    quizPassed: !!progress.quiz?.passed,
    quizBestScore: progress.quiz?.bestScore ?? null,
    labPassed: progress.lab.status === "passed",
    labScore: progress.lab.bestScore ?? null,
    labMaxScore: progress.lab.maxScore ?? lab.maxScore,
    complete: false,
  });

  const nextLesson = lessons.find((lesson) => !progress.lessons[lesson.id]) ?? lessons[0];

  return (
    <div className="reader">
      <header className="reader-head">
        <h1>{module.title}</h1>
        <div className="reader-meta">
          <span>{formatMinutes(module.estimatedMinutes)} in total</span>
          <span>{pluralise(lessons.length, "lesson")}</span>
          <span>{pluralise(quiz.questions.length, "quiz question")}</span>
          <span>
            {lab.maxScore} lab {lab.maxScore === 1 ? "point" : "points"}
          </span>
        </div>
      </header>

      <p className="prose" style={{ maxWidth: "58ch" }}>
        {module.summary}
      </p>

      <div className="gates">
        {gates.map((gate) => (
          <GateChip key={gate.key} state={gate.state} label={gate.label} />
        ))}
      </div>

      <section className="objectives" aria-labelledby="outcomes-heading">
        <h2 id="outcomes-heading">By the end of this module you can</h2>
        <ul>
          {module.outcomes.map((outcome) => (
            <li key={outcome}>{outcome}</li>
          ))}
        </ul>
      </section>

      <section className="panel" aria-labelledby="route-heading">
        <div className="panel-head">
          <h2 id="route-heading">What you will do</h2>
          <span className="dim mono">{module.id}</span>
        </div>
        <ol className="checklist">
          {lessons.map((lesson, index) => (
            <li className={progress.lessons[lesson.id] ? "check is-pass" : "check"} key={lesson.id}>
              <Icon
                name={progress.lessons[lesson.id] ? "check" : "circle"}
                size={14}
                className="check-glyph"
              />
              <div>
                <Link className="check-title" to={`${base}/lessons/${lesson.id}`}>
                  {index + 1}. {lesson.title}
                </Link>
                <p className="check-detail">{formatMinutes(lesson.minutes)} of reading</p>
              </div>
              <span className="check-points">{formatMinutes(lesson.minutes)}</span>
            </li>
          ))}
          <li className={progress.quiz?.passed ? "check is-pass" : "check"}>
            <Icon
              name={progress.quiz?.passed ? "check" : "circle"}
              size={14}
              className="check-glyph"
            />
            <div>
              <Link className="check-title" to={`${base}/quiz`}>
                Quiz
              </Link>
              <p className="check-detail">
                {progress.quiz
                  ? `${pluralise(progress.quiz.attempts, "attempt")}, best ${formatPercent(progress.quiz.bestScore)}. `
                  : ""}
                {formatPercent(quiz.passMark)} of the questions must be right.
              </p>
            </div>
            <span className="check-points">{quiz.questions.length} Q</span>
          </li>
          <li className={progress.lab.status === "passed" ? "check is-pass" : "check"}>
            <Icon
              name={progress.lab.status === "passed" ? "check" : "circle"}
              size={14}
              className="check-glyph"
            />
            <div>
              <Link className="check-title" to={`${base}/lab`}>
                Lab — {lab.title}
              </Link>
              <p className="check-detail">{lab.summary}</p>
            </div>
            <span className="check-points">
              {progress.lab.bestScore ?? 0}/{lab.maxScore}
            </span>
          </li>
        </ol>
      </section>

      <div className="lesson-foot">
        <Link className="btn btn-primary" to={`${base}/lessons/${nextLesson.id}`}>
          {lessonsDone === 0 ? "Start the first lesson" : "Continue the lessons"}
        </Link>
        <div className="pager">
          <Link className="btn" to={`/courses/${module.courseId}`}>
            <Icon name="arrowLeft" />
            Course map
          </Link>
          <Link className="btn" to={`${base}/lab`}>
            <Icon name="terminal" />
            Open the lab
          </Link>
        </div>
      </div>
    </div>
  );
}
